import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { SupportedLanguages } from '@/i18n/i18next';
import { useLanguage } from '@/shared/contexts/LanguageContext';

export const Route = createFileRoute('/_privacy/privacy-policy')({
  component: PrivacyPolicyPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      lang: (search.lang as SupportedLanguages | undefined) || undefined,
    };
  },
});

function PrivacyPolicyPage() {
  const { t } = useTranslation('privacyPolicy');
  const { setLanguage, language } = useLanguage();
  const navigate = useNavigate();
  const search = useSearch({ from: Route.id });

  // Change language when query parameter changes
  useEffect(() => {
    if (
      search.lang &&
      (search.lang === 'en' || search.lang === 'he') &&
      search.lang !== language
    ) {
      setLanguage(search.lang);
    }
  }, [search.lang, language, setLanguage]);

  const handleGoBack = () => {
    navigate({ to: '..' });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-8 md:px-6 lg:px-8 md:py-12">
        {/* Header */}
        <header className="mb-8 pb-6 border-b border-border text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
            {t('privacy_policy_content.company_name')}
          </h1>
          <h2 className="text-xl md:text-2xl text-muted-foreground mb-2">
            {t('privacy_policy_content.system_name')}
          </h2>
          <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            {t('privacy_policy')}
          </h3>
          <p className="text-sm md:text-base text-muted-foreground italic">
            {t('last_updated')}: {t('privacy_policy_date')}
          </p>
        </header>

        {/* Main Content */}
        <main className="prose prose-slate max-w-none dark:prose-invert">
          {/* Introduction Section */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              1. {t('privacy_policy_content.introduction.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>{t('privacy_policy_content.introduction.welcome')}</p>
              <p>{t('privacy_policy_content.introduction.terms')}</p>
              <p>{t('privacy_policy_content.introduction.important_notice')}</p>
              <p>{t('privacy_policy_content.introduction.updates_notice')}</p>
              <p>{t('privacy_policy_content.introduction.agreement_notice')}</p>
            </div>
          </section>

          {/* Information Collection */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              2. {t('privacy_policy_content.information_collection.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <h3 className="text-xl md:text-2xl font-bold text-foreground mt-6 mb-2">
                {t(
                  'privacy_policy_content.information_collection.system_access.title'
                )}
              </h3>
              <p>
                {t(
                  'privacy_policy_content.information_collection.system_access.content'
                )}
              </p>

              <h3 className="text-xl md:text-2xl font-bold text-foreground mt-6 mb-2">
                {t(
                  'privacy_policy_content.information_collection.user_accounts.title'
                )}
              </h3>
              <p>
                {t(
                  'privacy_policy_content.information_collection.user_accounts.admin_user'
                )}
              </p>
              <p>
                {t(
                  'privacy_policy_content.information_collection.user_accounts.account_management'
                )}
              </p>
              <p>
                {t(
                  'privacy_policy_content.information_collection.user_accounts.required_info'
                )}
              </p>
              <p>
                {t(
                  'privacy_policy_content.information_collection.user_accounts.profile_photos'
                )}
              </p>

              <p>
                {t(
                  'privacy_policy_content.information_collection.admin_permissions'
                )}
              </p>
              <p>
                {t(
                  'privacy_policy_content.information_collection.direct_manager'
                )}
              </p>
              <p>
                {t(
                  'privacy_policy_content.information_collection.content_monitoring'
                )}
              </p>
              <p>
                {t('privacy_policy_content.information_collection.usage_data')}
              </p>
              <p>
                {t(
                  'privacy_policy_content.information_collection.security_data'
                )}
              </p>
            </div>
          </section>

          {/* Information Security */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              3. {t('privacy_policy_content.information_security.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>
                {t('privacy_policy_content.information_security.content_1')}
              </p>
              <p>
                {t('privacy_policy_content.information_security.content_2')}
              </p>
              <p>
                {t('privacy_policy_content.information_security.content_3')}
              </p>
              <p>
                <strong>
                  {t('privacy_policy_content.information_security.content_4')}
                </strong>
              </p>
            </div>
          </section>

          {/* Policy Scope */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              4. {t('privacy_policy_content.policy_scope.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>{t('privacy_policy_content.policy_scope.content_1')}</p>
              <p>{t('privacy_policy_content.policy_scope.content_2')}</p>
            </div>
          </section>

          {/* Information Usage */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              5. {t('privacy_policy_content.information_usage.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>{t('privacy_policy_content.information_usage.intro')}</p>
              <ol className="list-decimal list-outside ms-6 space-y-2">
                <li>
                  {t(
                    'privacy_policy_content.information_usage.system_provision'
                  )}
                </li>
                <li>
                  {t('privacy_policy_content.information_usage.user_support')}
                </li>
                <li>
                  {t('privacy_policy_content.information_usage.statistics')}
                </li>
                <li>
                  {t(
                    'privacy_policy_content.information_usage.legal_compliance'
                  )}
                </li>
                <li>
                  {t(
                    'privacy_policy_content.information_usage.legal_procedures'
                  )}
                </li>
                <li>
                  {t(
                    'privacy_policy_content.information_usage.fraud_prevention'
                  )}
                </li>
                <li>
                  {t('privacy_policy_content.information_usage.security')}
                </li>
              </ol>
            </div>
          </section>

          {/* Information Sharing */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              6. {t('privacy_policy_content.information_sharing.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>{t('privacy_policy_content.information_sharing.suppliers')}</p>

              <h3 className="text-xl md:text-2xl font-bold text-foreground mt-6 mb-2">
                {t(
                  'privacy_policy_content.information_sharing.data_transfer.title'
                )}
              </h3>
              <p>
                {t(
                  'privacy_policy_content.information_sharing.data_transfer.no_sale'
                )}
              </p>
              <p>
                {t(
                  'privacy_policy_content.information_sharing.data_transfer.no_transfer'
                )}
              </p>
              <ol className="list-decimal list-outside ms-6 space-y-2">
                <li>
                  {t(
                    'privacy_policy_content.information_sharing.data_transfer.admin_instructions'
                  )}
                </li>
                <li>
                  {t(
                    'privacy_policy_content.information_sharing.data_transfer.legal_requirements'
                  )}
                </li>
                <li>
                  {t(
                    'privacy_policy_content.information_sharing.data_transfer.court_orders'
                  )}
                </li>
                <li>
                  {t(
                    'privacy_policy_content.information_sharing.data_transfer.business_reorganization'
                  )}
                </li>
                <li>
                  {t(
                    'privacy_policy_content.information_sharing.data_transfer.legal_proceedings'
                  )}
                </li>
                <li>
                  {t(
                    'privacy_policy_content.information_sharing.data_transfer.harm_prevention'
                  )}
                </li>
                <li>
                  {t(
                    'privacy_policy_content.information_sharing.data_transfer.affiliated_companies'
                  )}
                </li>
              </ol>
            </div>
          </section>

          {/* User Rights */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              7. {t('privacy_policy_content.user_rights.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>{t('privacy_policy_content.user_rights.access_requests')}</p>
              <p>{t('privacy_policy_content.user_rights.company_response')}</p>
            </div>
          </section>

          {/* Policy Changes */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              8. {t('privacy_policy_content.policy_changes.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>{t('privacy_policy_content.policy_changes.update_rights')}</p>
              <p>{t('privacy_policy_content.policy_changes.notification')}</p>
              <p>{t('privacy_policy_content.policy_changes.continued_use')}</p>
            </div>
          </section>

          {/* Children and Minors */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              9. {t('privacy_policy_content.children_minors.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>{t('privacy_policy_content.children_minors.content_1')}</p>
              <p>{t('privacy_policy_content.children_minors.content_2')}</p>
            </div>
          </section>

          {/* Ownership Changes */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              10. {t('privacy_policy_content.ownership_changes.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>{t('privacy_policy_content.ownership_changes.content_1')}</p>
            </div>
          </section>

          {/* Documentation and Previous Versions */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              11. {t('privacy_policy_content.documentation.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>{t('privacy_policy_content.documentation.content_1')}</p>
              <p>{t('privacy_policy_content.documentation.content_2')}</p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4 border-s-4 border-primary ps-4">
              12. {t('privacy_policy_content.contact.title')}
            </h2>
            <div className="space-y-4 text-base md:text-lg leading-relaxed">
              <p>{t('privacy_policy_content.contact.content_1')}</p>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="mt-8 text-center">
          <Button onClick={handleGoBack} variant="default" size="lg">
            {t('go_back')}
          </Button>
        </footer>
      </div>
    </div>
  );
}
